import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useRef, useEffect } from "react";
import DynamicIcon from "./dynamic-icon";
import ToggleHistory from "./toggle-history";
import IconHover from "./icon-hover";
import ComingSoonText from "./coming-soon-text";
import SidebarDropdown from "./sidebar-dropdown";
import Spinner from "./spinner";
import SideBarContext from "../../contexts/side-bar-context";
import { getCachedBusiness } from "../../lib/utils";

const NavMenuItem = ({
  href,
  children,
  isNew,
  icon,
  onClick,
  history,
  newTab,
  className = "",
  handleEllipsis,
  isEllipsisOpen,
  closeEllipsis,
  dropdownContent,
  isEditing,
  tableId,
  handleChangeName,
  toggleEditing,
  isSaving,
  isChild = false,
  normalizeRoute
}) => {
  const router = useRouter();
  const { setIsSideBarOpen } = useContext(SideBarContext);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  // Determine if this menu item is active
  const isActive = href === "/"
    ? router.pathname === href
    : router.pathname.includes(href) || router.asPath === href;

  // Focus input when editing mode is enabled
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const timeoutId = setTimeout(() => inputRef.current.focus(), 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isEditing]);

  // Handle input key events
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleChangeName(tableId, e.currentTarget.value);
      e.currentTarget.blur();
    }
  };

  // Close sidebar on mobile devices
  const closeMobileSidebar = () => {
    if (window.innerWidth < 1024) {
      setIsSideBarOpen(false);
    }
  };

  // Handle click events
  const handleClick = () => {
    onClick?.();
    closeEllipsis?.();
    if (href) {
      closeMobileSidebar();
    }
  };

  // Build full URL path
  const fullHref = normalizeRoute ? href : `/${getCachedBusiness()}${href}`;

  // Shared content classes
  const contentClasses = `
    ${className}
    flex flex-shrink-0 custom-text-grey-800 justify-start items-center
    w-full text-[12px] h-[35px] px-[27px] gap-[12px] lg:rounded-[8px]
    ${isActive ? "custom-bg-grey-50" : ""}
  `.trim();

  const textClasses = `
    ${isActive ? "custom-text-grey-800 leading-[16px]" : "custom-text-grey-500 text-[12px] tracking-[0.5px]"}
    truncate poppins-medium whitespace-nowrap
    ${isChild ? "max-w-[110px]" : "max-w-[132px]"}
  `.trim();

  // Render editing mode
  if (isEditing) {
    return (
      <div ref={triggerRef} className="relative">
        <div className="flex items-center justify-between custom-text-grey-500 relative group lg:rounded-[4px] lg:max-w-[214px] max-w-[239px] w-full custom-bg-white-50 text-[12px] h-[35px] px-[8px]">
          <input
            ref={inputRef}
            type="text"
            className="w-full outline-none poppins-medium bg-[transparent]"
            defaultValue={children}
            onBlur={(e) => handleChangeName(tableId, e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.target.select()}
          />
          {isSaving ? (
            <Spinner size={14} />
          ) : (
            <DynamicIcon
              name="x"
              color="cursor-pointer"
              size={14}
              onClick={toggleEditing}
            />
          )}
        </div>
      </div>
    );
  }

  // Render menu item content
  const renderContent = () => (
    <>
      {icon && <DynamicIcon name={icon} size="16" color="currentColor" />}
      <div className="flex items-center gap-[4px] w-full">
        <span className={textClasses}>{children}</span>
        {isNew && <ComingSoonText>New</ComingSoonText>}
      </div>
    </>
  );

  return (
    <div ref={triggerRef} className="relative">
      <div
        onClick={href ? closeMobileSidebar : handleClick}
        className="relative group hover:custom-bg-grey-50 lg:rounded-[8px] flex lg:max-w-[214px] max-w-[239px] w-full"
      >
        {href ? (
          <Link href={fullHref} legacyBehavior>
            <a
              onClick={closeEllipsis}
              target={newTab ? "_blank" : "_self"}
              className={contentClasses}
            >
              {renderContent()}
            </a>
          </Link>
        ) : (
          <div className={`${contentClasses} cursor-pointer`}>
            {renderContent()}
          </div>
        )}

        {history && (
          <IconHover additionalclasses="absolute right-2 top-[4px] z-[10]">
            <ToggleHistory size={16} color="" />
          </IconHover>
        )}

        {handleEllipsis && (
          <>
            <IconHover
              onClick={(e) => {
                e.stopPropagation();
                handleEllipsis();
              }}
              additionalclasses="!hidden lg:!block absolute right-2 top-[4px] z-[10] opacity-0 group-hover:opacity-100"
            >
              <DynamicIcon name="ellipsis" size="16" color="custom-text-grey-800" />
            </IconHover>
            <SidebarDropdown
              additionalclasses="!min-w-[181px]"
              triggerRef={triggerRef}
              position="right"
              isOpen={isEllipsisOpen}
              toggleDropdown={closeEllipsis}
            >
              {dropdownContent}
            </SidebarDropdown>
          </>
        )}
      </div>
    </div>
  );
};

export default NavMenuItem;